import React, { useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import Text from '../common/Text';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

export interface MenuItem {
  key: string;
  label: string;
  icon: string;
}

interface DashboardLayoutProps {
  title: string;
  menuItems: MenuItem[];
  activeView: string;
  onViewChange: (view: string) => void;
  children: React.ReactNode;
  loading?: boolean;
  onLogout?: () => void;
  pageTitle?: string;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  title: _title,
  menuItems,
  activeView,
  onViewChange,
  children,
  loading = false,
  onLogout,
  pageTitle: _pageTitle,
}) => {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile, screenWidth } = useResponsiveLayout();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const sidebarWidth = isMobile ? screenWidth * 0.8 : 200;

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.layout}>
        {/* Sidebar */}
        {(sidebarOpen || !isMobile) && (
          <View
            style={[
              styles.sidebar,
              {
                backgroundColor: colors.card,
                borderRightColor: colors.border,
                width: sidebarWidth,
              },
              isMobile && {
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: 100,
                elevation: 5,
              },
            ]}
          >
            {isMobile && (
              <View style={styles.sidebarHeader}>
                <TouchableOpacity
                  onPress={() => setSidebarOpen(false)}
                  style={styles.closeButton}
                >
                  <Ionicons name="close" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            )}

            <View style={styles.sidebarContent}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {menuItems.map((item) => (
                  <TouchableOpacity
                    key={item.key}
                    style={[
                      styles.menuItem,
                      activeView === item.key && {
                        backgroundColor: colors.primary,
                      },
                    ]}
                    onPress={() => {
                      onViewChange(item.key);
                      if (isMobile) setSidebarOpen(false);
                    }}
                  >
                    <Ionicons
                      name={item.icon as keyof typeof Ionicons.glyphMap}
                      size={18}
                      color={
                        activeView === item.key ? colors.card : colors.text
                      }
                      style={styles.menuIcon}
                    />
                    <Text
                      style={[
                        styles.menuText,
                        {
                          color:
                            activeView === item.key ? colors.card : colors.text,
                        },
                      ]}
                    >
                      {item.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              {onLogout && (
                <TouchableOpacity
                  style={[styles.menuItem, styles.logoutItem]}
                  onPress={onLogout}
                >
                  <Ionicons
                    name="log-out-outline"
                    size={18}
                    color={colors.errorText || '#ef4444'}
                    style={styles.menuIcon}
                  />
                  <Text
                    style={[
                      styles.menuText,
                      { color: colors.errorText || '#ef4444' },
                    ]}
                  >
                    Logout
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {/* Main Content */}
        <View
          style={[
            styles.mainContent,
            Platform.OS === 'web' && !isMobile && styles.mainContentWeb,
          ]}
        >
          {/* Mobile Menu Button */}
          {isMobile && !sidebarOpen && (
            <TouchableOpacity
              style={[
                styles.menuButton,
                { backgroundColor: colors.card, borderColor: colors.border },
              ]}
              onPress={() => setSidebarOpen(true)}
            >
              <Ionicons name="menu" size={24} color={colors.text} />
            </TouchableOpacity>
          )}

          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={{ paddingBottom: insets.bottom + 20 }}
            showsVerticalScrollIndicator={false}
          >
            <View style={[styles.content, isMobile && styles.contentMobile]}>
              {loading ? (
                <View style={styles.loadingContainer}>
                  <Text style={[styles.loading, { color: colors.secondary }]}>
                    Loading...
                  </Text>
                </View>
              ) : (
                children
              )}
            </View>
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? {
          margin: 0,
          padding: 0,
          width: '100%',
        }
      : {}),
  },
  layout: {
    flex: 1,
    flexDirection: 'row',
    ...(Platform.OS === 'web'
      ? {
          margin: 0,
          padding: 0,
          width: '100%',
        }
      : {}),
  },
  sidebar: {
    borderRightWidth: 1,
    ...(Platform.OS === 'web'
      ? {
          minWidth: 200,
          maxWidth: 200,
          position: 'absolute' as const,
          left: 0,
          top: 0,
          bottom: 0,
          height: '100%',
          zIndex: 1,
          margin: 0,
          padding: 0,
        }
      : {}),
  },
  sidebarHeader: {
    padding: 10,
    paddingHorizontal: 12,
    paddingLeft: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  pageTitle: {
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
  },
  closeButton: {
    padding: 0,
    margin: 0,
  },
  sidebarTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  sidebarContent: {
    flex: 1,
    flexDirection: 'column',
    ...(Platform.OS === 'web'
      ? {
          // @ts-ignore - web-only CSS
          scrollbarWidth: 'none',
          // @ts-ignore - web-only CSS
          msOverflowStyle: 'none',
        }
      : {}),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    paddingHorizontal: 12,
    paddingLeft: 8,
    marginHorizontal: 4,
    marginVertical: 2,
    borderRadius: 6,
  },
  menuIcon: {
    marginRight: 10,
  },
  menuText: {
    fontSize: 12,
    fontWeight: '500',
  },
  logoutItem: {
    marginTop: 'auto',
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
    paddingTop: 10,
  },
  mainContent: {
    flex: 1,
  },
  mainContentWeb: {
    marginLeft: 200,
    padding: 0,
  },
  menuButton: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 10,
    padding: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  scrollView: {
    flex: 1,
    ...(Platform.OS === 'web'
      ? {
          // @ts-ignore - web-only CSS
          scrollbarWidth: 'none',
          // @ts-ignore - web-only CSS
          msOverflowStyle: 'none',
        }
      : {}),
  },
  content: {
    padding: 20,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  contentMobile: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loading: {
    fontSize: 16,
  },
});

export default DashboardLayout;
